import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MobileStepper,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  MoreVert,
  EditOutlined,
  DeleteOutline,
  FileUploadOutlined,
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ZoomIn,
  ZoomOut,
  FitScreen,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { StaffService } from "../../../services/staffandplant/staffService";
import {
  StaffAvatar,
  JobTitleChip,
  themeColors,
  commonPaperStyles,
  getProjectNames,
} from "../../../utils/content";
import { isReadOnly } from "../../../utils/permissionUtils";
import { CustomPagination } from '../../../shared/Helper';

const StaffTable = ({
  staff,
  loading,
  activeTab,
  settings,
  onEdit,
  onDelete,
  onImport,
  onDeallocate,
}) => {
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [currentStaffForAction, setCurrentStaffForAction] = useState(null);

  // Document preview states
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewDocs, setPreviewDocs] = useState([]);
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [imageScale, setImageScale] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);

  const allProjects = settings?.projectList || [];

  const handleActionMenuClick = (event, staffMember) => {
    setActionMenuAnchor(event.currentTarget);
    setCurrentStaffForAction(staffMember);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setCurrentStaffForAction(null);
  };

  // Document preview functions
  const fetchFileUrl = async (uploadId) => {
    try {
      return await StaffService.getFileUrl(uploadId, true);
    } catch (error) {
      console.error("Error fetching file URL:", error);
      return "";
    }
  };

  const handleViewPhotoId = async (staffMember) => {
    const uploadId = staffMember.user?.profile_data?.photo_id;
    if (!uploadId) return;

    try {
      const url = await fetchFileUrl(uploadId);
      handlePreviewDocument(
        url,
        `${staffMember.user.name}'s Photo ID`,
        null,
        true
      );
    } catch (error) {
      console.error("Error viewing photo ID:", error);
    }
  };

  const handleViewAllDocuments = async (staffMember) => {
    const docs = staffMember.user?.profile_data?.other_documents || [];

    if (docs.length === 0) {
      console.warn("No documents available");
      return;
    }

    try {
      const formattedDocs = await Promise.all(
        docs.map(async (doc) => {
          if (!doc.upload_id) {
            return {
              url: "",
              name: doc.name || "Document",
              type: doc.type,
            };
          }

          try {
            const url = await fetchFileUrl(doc.upload_id);
            return {
              url: url || "",
              name: doc.name || "Document",
              type: doc.type,
            };
          } catch (error) {
            console.error(`Error fetching document ${doc.upload_id}:`, error);
            return {
              url: "",
              name: doc.name || "Document",
              type: doc.type,
            };
          }
        })
      );

      setPreviewDocs(formattedDocs);
      setPreviewTitle(`${staffMember.user?.name}'s Documents`);
      setActiveDocIndex(0);
      setPreviewUrl(formattedDocs[0]?.url || "");
      setOpenPreviewModal(true);
    } catch (error) {
      console.error("Error loading documents:", error);
    }
  };

  const handlePreviewDocument = (
    url,
    title,
    docs = null,
    isSingleDoc = false
  ) => {
    url = url != null ? String(url) : "";

    if (url && !url.startsWith("http")) {
      url = `${process.env.REACT_APP_API}/api/file_upload/download?id=${url}&org=True`;
    }

    if (isSingleDoc) {
      setPreviewDocs([{ url, name: title, type: 0 }]);
      setActiveDocIndex(0);
    } else if (docs && Array.isArray(docs)) {
      setPreviewDocs(docs);
      setActiveDocIndex(0);
      url = docs[0]?.url || "";
    }

    setPreviewUrl(url);
    setPreviewTitle(title || "Document Preview");
    setImageScale(1);
    setOpenPreviewModal(true);
  };

  const handleDocumentNavigation = (direction) => {
    if (previewDocs.length <= 1) return;

    let newIndex;
    if (direction === "next") {
      newIndex = (activeDocIndex + 1) % previewDocs.length;
    } else {
      newIndex = (activeDocIndex - 1 + previewDocs.length) % previewDocs.length;
    }

    setActiveDocIndex(newIndex);
    setPreviewUrl(previewDocs[newIndex].url);
    setPreviewTitle(
      `${previewTitle.split(" - ")[0]} - ${previewDocs[newIndex].name}`
    );
    setImageScale(1);
  };

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.25, 0.25));
  };

  const handleFitToScreen = () => {
    setImageScale(1);
  };

  const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    const urlLower = url.toLowerCase();
    return (
      imageExtensions.some((ext) => urlLower.includes(ext)) ||
      url.includes("image") ||
      url.includes("photo")
    );
  };

  const getFileExtension = (url, docType) => {
    if (!url) return "";

    const urlParts = url.split(".");
    if (urlParts.length > 1) {
      const ext = urlParts[urlParts.length - 1].split("?")[0].split("#")[0];
      if (ext && ext.length <= 5) {
        return `.${ext.toLowerCase()}`;
      }
    }

    if (isImageFile(url)) {
      if (url.includes("jpeg") || url.includes("jpg")) return ".jpg";
      if (url.includes("png")) return ".png";
      if (url.includes("gif")) return ".gif";
      if (url.includes("webp")) return ".webp";
      if (url.includes("svg")) return ".svg";
      return ".jpg";
    }

    if (url.includes("pdf")) return ".pdf";
    if (url.includes("doc")) return ".doc";
    if (url.includes("docx")) return ".docx";
    if (url.includes("xls")) return ".xls";
    if (url.includes("xlsx")) return ".xlsx";
    if (url.includes("ppt")) return ".ppt";
    if (url.includes("pptx")) return ".pptx";
    if (url.includes("txt")) return ".txt";

    return "";
  };

  const handleDownload = async () => {
    if (!previewUrl) {
      console.error("No preview URL available for download");
      return;
    }

    try {
      const getContentType = (filename) => {
        const ext = filename.toLowerCase().split(".").pop();
        const contentTypes = {
          pdf: "application/pdf",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          gif: "image/gif",
          webp: "image/webp",
          bmp: "image/bmp",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          xls: "application/vnd.ms-excel",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          txt: "text/plain",
          zip: "application/zip",
          rar: "application/x-rar-compressed",
        };
        return contentTypes[ext] || "application/octet-stream";
      };

      const currentDoc = previewDocs[activeDocIndex];
      const docName = currentDoc?.name || previewTitle || "document";
      const fileExtension = getFileExtension(previewUrl, currentDoc?.type);

      let fileName = docName;
      if (!fileName.includes(".") && fileExtension) {
        fileName += fileExtension;
      }
      fileName = fileName.replace(/[<>:"/\\|?*]/g, "_");

      let fileId = null;
      if (previewUrl.includes("id=")) {
        const urlParams = new URLSearchParams(previewUrl.split("?")[1]);
        fileId = urlParams.get("id");
      }

      if (!fileId && currentDoc && (currentDoc.upload_id || currentDoc.id)) {
        fileId = currentDoc.upload_id || currentDoc.id;
      }

      if (fileId) {
        const downloadUrl = await StaffService.getFileUrl(fileId, true);

        if (
          !downloadUrl ||
          typeof downloadUrl !== "string" ||
          !downloadUrl.startsWith("http")
        ) {
          throw new Error("Invalid download URL received from server");
        }

        const fileResponse = await fetch(downloadUrl);

        if (!fileResponse.ok) {
          throw new Error(
            `Failed to fetch file: ${fileResponse.status} ${fileResponse.statusText}`
          );
        }

        const contentType =
          fileResponse.headers.get("content-type") || getContentType(fileName);

        const arrayBuffer = await fileResponse.arrayBuffer();

        const blob = new Blob([arrayBuffer], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return;
      }

      const response = await fetch(previewUrl);

      if (!response.ok) {
        throw new Error(`Fallback fetch failed: ${response.status}`);
      }

      const contentType =
        response.headers.get("content-type") || getContentType(fileName);
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: contentType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);

      try {
        const currentDoc = previewDocs[activeDocIndex];
        const docName = currentDoc?.name || previewTitle || "document";
        const link = document.createElement("a");
        link.href = previewUrl;
        link.setAttribute("download", docName);
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (lastResortError) {
        console.error("All download methods failed:", lastResortError);
        window.open(previewUrl, "_blank");
      }
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Staff List",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const staffMember = params.row;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <StaffAvatar staff={staffMember} size={36} fontSize={16} />
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {staffMember.user?.name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {staffMember.user?.email || "-"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const pd = params.row.user?.profile_data;
        const countryCode = pd?.country_code || "";
        const num = pd?.mobile_number || pd?.number;
        return num ? `${countryCode} ${num}` : "-";
      },
    },
    {
      field: "jobTitle",
      headerName: "Job Title",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <JobTitleChip
            jobTitle={params.row.user?.job_title}
            roleAuth={params.row.user?.profile_data?.role_auth}
          />
        );
      },
    },
    ...(activeTab === 1
      ? [
          {
            field: "projects",
            headerName: "Projects",
            flex: 1.2,
            minWidth: 180,
            renderCell: (params) => {
              if (!params || !params.row) return "-";
              const staffMember = params.row;
              let projectNames = [];

              if (
                Array.isArray(staffMember.projects) &&
                staffMember.projects.length > 0
              ) {
                if (
                  typeof staffMember.projects[0] === "string" ||
                  typeof staffMember.projects[0] === "number"
                ) {
                  projectNames = getProjectNames(
                    staffMember.projects.map(Number),
                    allProjects
                  );
                } else {
                  projectNames = staffMember.projects.map(
                    (p) =>
                      p.project_name ||
                      p.name ||
                      getProjectNames([p.project_id], allProjects)[0] ||
                      p.project_id
                  );
                }
              }

              return (
                <Typography variant="body2">
                  {projectNames.length > 0 ? projectNames.join(", ") : "-"}
                </Typography>
              );
            },
          },
        ]
      : []),
    {
      field: "photoId",
      headerName: "Photo ID",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        return params.row.user?.profile_data?.photo_id ? (
          <Button
            variant="text"
            size="small"
            sx={{ color: "#1976D2" }}
            onClick={() => handleViewPhotoId(params.row)}
          >
            View ID
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      },
    },
    {
      field: "otherDocs",
      headerName: "Other Docs",
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const documents = params.row.user?.profile_data?.other_documents || [];
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              gap: 0.5,
            }}
          >
            {documents.length > 0 ? (
              <>
                <Chip
                  label={`${documents.length} doc${
                    documents.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0 }}
                />
                <Typography
                  variant="caption"
                  component="a"
                  onClick={() => handleViewAllDocuments(params.row)}
                  color="primary"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                    lineHeight: 1.2,
                  }}
                >
                  View Docs
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                —
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Actions" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleActionMenuClick(e, params.row)}
            sx={{
              borderRadius: 2,
              background: "#f5f5f5",
              "&:hover": { background: "#ffeaea" },
            }}
          >
            <MoreVert />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (loading) {
    return (
      <Paper {...commonPaperStyles}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "71vh",
          }}
        >
          <CircularProgress sx={{ color: themeColors.primary }} />
        </Box>
      </Paper>
    );
  }

  if (!staff || staff.length === 0) {
    return (
      <Paper
        {...commonPaperStyles}
        sx={{ mx: 2, borderRadius: 2, overflow: "hidden" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 5,
            height: 400,
          }}
        >
          <Box
            component="img"
            src="/EmptyStat.svg"
            sx={{ width: 150, height: 150, opacity: 0.7, mb: 3 }}
            alt="No data"
          />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Staff Found
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {activeTab === 0
              ? "Use the Import Staff button to add staff to your project"
              : "Use the Add Staff button to create a new staff member"}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Box sx={{ height: "71vh", width: "100%", backgroundColor: "#ffffff" }}>
        <DataGrid
          rows={staff.map((row, idx) => ({
            ...row,
            id: row.id || row.user?.id || `temp-${idx}`,
          }))}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          disableColumnFilter
          disableColumnSorting
          disableColumnMenu
          disableRowSelectionOnClick
          slots={{ pagination: CustomPagination }}
          sx={{
            "& .MuiDataGrid-columnSeparator": {
              color: "#fff",
            },
            "& .MuiDataGrid-columnHeader": {
              fontWeight: 600,
              backgroundColor: "#f16253",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              width: "100%",
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{
          sx: { boxShadow: "0 2px 10px rgba(0,0,0,0.1)" },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            onEdit(currentStaffForAction);
            handleActionMenuClose();
          }}
          sx={{ minWidth: 120 }}
          disabled ={isReadOnly()}
        >
          <ListItemIcon>
            <EditOutlined fontSize="small" sx={{ color: "#1976D2" }} />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        {activeTab === 0 ? (
          <MenuItem
            onClick={() => {
              onDeallocate(currentStaffForAction);
              handleActionMenuClose();
            }}
            sx={{ color: "#F44336" }}
            disabled ={isReadOnly()}
          >
            <ListItemIcon>
              <DeleteOutline fontSize="small" sx={{ color: "#F44336" }}  />
            </ListItemIcon>
            <ListItemText primary="Remove" />
          </MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                onDelete(currentStaffForAction);
                handleActionMenuClose();
              }}
              sx={{ color: "#F44336" }}
              disabled ={isReadOnly()}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" sx={{ color: "#F44336" }} />
              </ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                onImport(
                  currentStaffForAction.id || currentStaffForAction.user?.id
                );
                handleActionMenuClose();
              }}
              disabled ={isReadOnly()}
            >
              <ListItemIcon>
                <FileUploadOutlined
                  fontSize="small"
                  sx={{ color: "#4CAF50" }}
                />
              </ListItemIcon>
              <ListItemText primary="Import to Project" />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Document Preview Modal */}
      <Dialog
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: "95vh", sm: "90vh" },
            maxHeight: "95vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" noWrap>
                {previewTitle}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Zoom Controls for Images */}
                {isImageFile(previewUrl) && (
                  <>
                    <Tooltip title="Zoom Out">
                      <IconButton
                        onClick={handleZoomOut}
                        disabled={imageScale <= 0.25}
                        size="small"
                      >
                        <ZoomOut />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{ minWidth: "40px", textAlign: "center" }}
                    >
                      {Math.round(imageScale * 100)}%
                    </Typography>
                    <Tooltip title="Zoom In">
                      <IconButton
                        onClick={handleZoomIn}
                        disabled={imageScale >= 3}
                        size="small"
                      >
                        <ZoomIn />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fit to Screen">
                      <IconButton onClick={handleFitToScreen} size="small">
                        <FitScreen />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <IconButton onClick={() => setOpenPreviewModal(false)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="subtitle2" noWrap sx={{ width: "100%" }}>
              {previewDocs[activeDocIndex]?.name || ""}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 0,
            overflow: "hidden",
            height: "100%",
            minHeight: 0,
            bgcolor: "#f5f5f5",
          }}
        >
          {previewUrl ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  overflow: isImageFile(previewUrl) ? "auto" : "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                {isImageFile(previewUrl) ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    {imageLoading && (
                      <CircularProgress
                        sx={{
                          position: "absolute",
                          color: themeColors.primary,
                        }}
                      />
                    )}
                    <img
                      src={previewUrl}
                      alt={previewTitle}
                      onLoad={() => setImageLoading(false)}
                      onLoadStart={() => setImageLoading(true)}
                      style={{
                        maxWidth: imageScale === 1 ? "100%" : "none",
                        maxHeight: imageScale === 1 ? "100%" : "none",
                        width:
                          imageScale !== 1 ? `${imageScale * 100}%` : "auto",
                        height: "auto",
                        objectFit: "contain",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        backgroundColor: "white",
                        display: imageLoading ? "none" : "block",
                      }}
                    />
                  </Box>
                ) : (
                  <iframe
                    src={previewUrl}
                    title={previewTitle}
                    style={{
                      border: "none",
                      width: "100%",
                      height: "100%",
                      minHeight: 0,
                      display: "block",
                      borderRadius: "8px",
                      backgroundColor: "white",
                    }}
                  />
                )}
              </Box>

              {previewDocs.length > 1 && (
                <MobileStepper
                  variant="text"
                  steps={previewDocs.length}
                  position="static"
                  activeStep={activeDocIndex}
                  nextButton={
                    <Button
                      size="small"
                      onClick={() => handleDocumentNavigation("next")}
                      sx={{
                        color: "#1976D2",
                        "&:hover": { backgroundColor: "#f0f7ff" },
                      }}
                    >
                      Next
                      <KeyboardArrowRight />
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      onClick={() => handleDocumentNavigation("prev")}
                      sx={{
                        color: "#1976D2",
                        "&:hover": { backgroundColor: "#f0f7ff" },
                      }}
                    >
                      <KeyboardArrowLeft />
                      Back
                    </Button>
                  }
                  sx={{ bgcolor: "#fff" }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No document to preview
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            onClick={() => setOpenPreviewModal(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Box>
            {previewDocs.length > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 2 }}
              >
                Use navigation buttons to view all documents
              </Typography>
            )}
            <Button
              onClick={handleDownload}
              disabled={!previewUrl}
              variant="contained"
              sx={{
                backgroundColor: "#1976D2",
                "&:hover": { backgroundColor: "#1565C0" },
                textTransform: "none",
              }}
            >
              Download
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StaffTable;